import type { PropsWithChildren } from 'react'
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react'

import { Helmet, HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from 'styled-components'

import { useBundle } from 'hooks/useBundle'
import { useSystemThemeVariant } from 'hooks/useSystemThemeVariant'
import {
  getLanguage,
  getThemeVariant,
  setLanguage,
  setThemeVariant,
} from 'storage'
import { Mode } from 'theme/constants'
import { GlobalStyle } from 'theme/global'
import { defaultLightTheme, defaultTheme } from 'theme/theme'
import getEnv from 'utils/getEnv'
import { useTranslation } from 'utils/translation'

import type { Bundle } from './types/bundle'

// eslint-disable-next-line import/order
import { useLocalStorageState } from 'hooks/useLocalStorageState'

type AppContextProps = {
  bundleLoader: Bundle | null
  contentLoaded: boolean
  loadFailure: boolean
  settingsOpen: boolean
  setSettingsOpen: (open: boolean) => void
  themeVariant: Mode
  setThemeVariant: (theme: Mode | null) => void
}

const noop = (): void => {}

export const AppContext = createContext<AppContextProps>({
  bundleLoader: null,
  contentLoaded: false,
  loadFailure: false,
  settingsOpen: false,
  setSettingsOpen: noop,
  themeVariant: Mode.DARK,
  setThemeVariant: noop,
})

const env = getEnv()

export const AppProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const {
    i18n: { language },
  } = useTranslation()
  const [reload, setReload] = useState<boolean>(false)
  const [bundleLoader, setBundleLoader] = useState<Bundle | null>(null)
  const [contentLoaded, setContentLoaded] = useState(false)
  const [loadFailure, setLoadFailure] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const { fullBundle } = useBundle()

  // Set Language to `en` if no language is selected`
  if (!getLanguage()) setLanguage('en')

  /**
   *
   * This functionality is in place to take care of vh measurements on iOS safari,
   * where the browser chrome-elements interrupt a typical measurement of 100vh
   */
  useLayoutEffect(() => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    window.addEventListener('resize', () => {
      // We execute the same script as before
      const intVh = window.innerHeight
      document.documentElement.style.setProperty('--vh', `${intVh}px`)
    })
  }, [])

  const [themeVariant, setCtxThemeVariant] = useLocalStorageState(
    getThemeVariant,
    setThemeVariant,
  )

  const systemThemeVariant = useSystemThemeVariant()

  useEffect(() => {
    async function setup(): Promise<void> {
      try {
        const response = await fullBundle()
        setBundleLoader(response)
        setContentLoaded(true)
      } catch (e) {
        setLoadFailure(true)
        console.error('Bundle setup failed', e)
      }
    }
    setup()
  }, [])

  useEffect(() => {
    async function load(): Promise<void> {
      setContentLoaded(false)
      setLoadFailure(false)
      setReload(false)
      try {
        const response = await fullBundle()
        setTimeout(() => {
          setBundleLoader(response)
          setContentLoaded(true)
        }, 1000)
      } catch (e) {
        setLoadFailure(true)
        console.error('Reload Bundle failed', e)
      }
    }

    if (reload) {
      load()
    }
  }, [reload])

  // Auto-refresh every 2 hours
  useEffect(() => {
    const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const autoRefreshInterval = setInterval(() => {
      setReload(true);
    }, twoHoursInMs);

    // Clean up the interval when the component unmounts
    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, []);

  const theme = themeVariant === Mode.LIGHT ? defaultLightTheme : defaultTheme

  return (
    <AppContext.Provider
      value={{
        bundleLoader,
        loadFailure,
        contentLoaded,
        settingsOpen,
        setSettingsOpen,
        themeVariant: themeVariant ?? systemThemeVariant,
        setThemeVariant: setCtxThemeVariant,
      }}
    >
      <ThemeProvider theme={theme}>
        <GlobalStyle theme={theme} />
        <HelmetProvider context={{}}>
          <Helmet
            htmlAttributes={{ lang: language }}
            titleTemplate={`%s - ${env.VITE_APP_TITLE}`}
            defaultTitle={env.VITE_APP_TITLE}
          />
          {children}
        </HelmetProvider>
      </ThemeProvider>
    </AppContext.Provider>
  )
}

export function useAppContext(): AppContextProps {
  const context = useContext<AppContextProps>(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within a AppProvider')
  }
  return context
}
